import type { PropsWithChildren, ReactNode } from "react";
import { useState } from "react";
import { styled } from "styled-components";

import { ExpandButton } from "common/components";
import { navbarComplementHeight } from "common/constants/navbarHeight";
import { breakpoints, createScrollbarStyle } from "common/styles";

type MainLayoutProps = PropsWithChildren & {
  headerElement?: ReactNode;
  sidePanel?: ReactNode;
};

export const MainLayout = ({ children, headerElement, sidePanel }: MainLayoutProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(!!sidePanel);

  return (
    <Flexbox>
      <Phantom isOpen={isOpen} />
      <Sidebar inert={!isOpen} isOpen={isOpen}>
        {sidePanel}
      </Sidebar>
      <Content isOpen={isOpen}>
        {headerElement && <Header>{headerElement}</Header>}
        {children}
      </Content>
      {sidePanel && <Expand isOpen={isOpen} onClick={() => setIsOpen((value) => !value)} />}
    </Flexbox>
  );
};

const Flexbox = styled.div`
  display: flex;
`;

const Phantom = styled.div<{ isOpen: boolean }>`
  display: none;

  @media ${breakpoints.extraLarge} {
    display: block;
    padding-right: ${({ isOpen }) => (isOpen ? 0.5 : 0)}rem;
    transition:
      padding-right 0.3s ease,
      width 0.3s ease;
    width: ${({ isOpen }) => (isOpen ? 350 : 0)}px;
  }
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  display: none;

  @media ${breakpoints.extraLarge} {
    box-sizing: border-box;
    display: block;
    height: ${navbarComplementHeight};
    overflow: auto;
    padding: 3rem 1rem 0 2rem;
    position: fixed;
    transform: translate(${({ isOpen }) => (isOpen ? 0 : -350)}px, 0);
    transition: transform 0.3s ease;
    width: 350px;

    ${createScrollbarStyle()}
  }
`;

const Content = styled.div<{ isOpen: boolean }>`
  width: 100%;

  @media ${breakpoints.extraLarge} {
    transition: width 0.3s ease;
    width: calc(100% - ${({ isOpen }) => (isOpen ? 350 : 0)}px);
  }
`;

const Header = styled.div`
  display: flex;

  @media ${breakpoints.extraLarge} {
    display: none;
  }
`;

const Expand = styled(ExpandButton)`
  display: none;
  left: 3rem;
  position: fixed;

  @media ${breakpoints.extraLarge} {
    display: flex;
  }
`;
